import { z } from "zod";

import { snapshotSchema } from "@/data/contract/v2";

/**
 * Issued revisions (D-062 rule 3; `crates/services/doc/src/files/issues`): when a transmittal is issued, each drawing in
 * it records the version of every xref it follows, so the issued revision keeps showing those versions while the
 * working drawing follows the latest. The routes are per drawing, by its ID on the doc service.
 */

/** One xref as it was at issue: the project file and the version the issued revision uses. */
export const frozenXrefSchema = z.object({
  /** The xref's name in the drawing. */
  name: z.string(),
  fileId: z.string(),
  version: z.number().int().positive(),
  /** The project file's name, "Structure.dxf". */
  fileName: z.string().min(1),
});

export const issueRecordSchema = z.object({
  /** What was issued: the transmittal's ID. */
  issue: z.string().min(1),
  label: z.string(),
  drawingVersion: z.number().int().nonnegative(),
  issuedBy: z.string(),
  /** ISO 8601, UTC. */
  issuedAt: z.string(),
  xrefs: z.array(frozenXrefSchema),
});

/** The issued revision to open read-only: its record and the drawing as it was at issue, its xrefs at their frozen versions. */
export const openedIssueSchema = z.object({ issue: issueRecordSchema, snapshot: snapshotSchema });
export type OpenedIssue = z.infer<typeof openedIssueSchema>;

export const issueListSchema = z.object({ issues: z.array(issueRecordSchema) });

export type FrozenXref = z.infer<typeof frozenXrefSchema>;
export type IssueRecord = z.infer<typeof issueRecordSchema>;

/** A drawing's sheets as the doc service lists them (`crates/services/doc/src/sheets`): its paper-space layouts. */
export const drawingSheetsSchema = z.object({
  drawing: z.string(),
  /** The drawing's version, which changes with every edit: what a thumbnail is cached by. */
  version: z.number().int().nonnegative(),
  /** When it was last saved (Unix ms); none for a drawing never edited. */
  savedAt: z.number().nullable(),
  /** Never issued, issued as it is, or changed since its latest issue. */
  status: z.enum(["ready", "issued", "changed"]),
  issue: z.object({ label: z.string(), issuedAt: z.string() }).nullable(),
  sheets: z.array(
    z.object({
      layout: z.string(),
      widthMm: z.number().positive(),
      heightMm: z.number().positive(),
    }),
  ),
});
export type DrawingSheets = z.infer<typeof drawingSheetsSchema>;
export const projectSheetsSchema = z.object({ drawings: z.array(drawingSheetsSchema) });

/** What freezing a drawing for an issue sends. */
export interface FreezeIssue {
  /** The transmittal's ID. */
  readonly issue: string;
  /** "For approval TR-001". */
  readonly label: string;
}

/**
 * The issue calls, which only the doc service answers fully. `projectId` is for files kept in this browser, which have
 * no drawing index: the service ignores it.
 */
export interface IssuedRevisionsApi {
  /** Freezes the drawing's xrefs for an issue (needs edit rights); issuing twice keeps the first record. */
  freezeIssue?(projectId: string, drawingId: string, body: FreezeIssue): Promise<IssueRecord>;
  /** The drawing's issues, newest first. */
  drawingIssues?(projectId: string, drawingId: string): Promise<readonly IssueRecord[]>;
  /** Where the issued revision's DXF downloads from, its frozen xrefs bound. */
  issuedDxfUrl?(projectId: string, drawingId: string, issue: string): string | undefined;
  /** The issued revision to open read-only in Draft (nothing syncs or is written). */
  openIssuedRevision?(projectId: string, drawingId: string, issue: string): Promise<OpenedIssue>;
  /** The drawing's layouts, from the drawing the doc service holds. */
  drawingSheets?(projectId: string, drawingId: string): Promise<DrawingSheets>;
  /** The lists of the drawings the caller may open, in one request (the others are left out). */
  projectSheets?(projectId: string, drawingIds: readonly string[]): Promise<DrawingSheets[]>;
  /** The issued revision plotted as a PDF. */
  issuedPdf?(projectId: string, drawingId: string, issue: string): Promise<Blob>;
}

/** "Structure.dxf · Frozen at issue (v3)". */
export function frozenText(xref: Pick<FrozenXref, "fileName" | "version">): string {
  return `${xref.fileName} · Frozen at issue (v${String(xref.version)})`;
}
